'use client';

import { UzFormField, UzInput } from '@utazzo/ui';
import { useEffect } from 'react';
import { type UseFormReturn, useFormContext, useWatch } from 'react-hook-form';

/**
 * Derive a master `code` from the human-readable `name`.
 *
 *   "International Package"  → "international_package"
 *   "Quoted - negotiation"   → "quoted_negotiation"
 *   "AT&T Inc."              → "at_t_inc"
 *
 * Output matches the shared `MasterCodeSchema` regex (`^[a-z0-9_]+$`): lower-
 * cased, non-alphanumerics collapsed to underscores, no leading/trailing/
 * repeated underscores.
 */
export const deriveCode = (name: string): string =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

/**
 * Headless companion to `MasterCodeField`: when a master form hides the
 * code input (the operator only types the name), the form state still
 * needs a valid `code` for Zod to accept the create payload. This hook
 * watches the source field and writes the derived code into the target
 * field as the operator types - same effect the visible MasterCodeField
 * runs internally, but without rendering anything.
 *
 * Pass the same RHF `UseFormReturn` you handed to `<UzForm>`. Source
 * defaults to `name`, target defaults to `code` - override for masters
 * that diverge from the family convention.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useAutoDeriveCode<TForm extends Record<string, any>>(
  form: UseFormReturn<TForm>,
  isCreate: boolean,
  options: { sourceField?: string; targetField?: string } = {},
): void {
  const { sourceField = 'name', targetField = 'code' } = options;
  const sourceValue = useWatch({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    control: form.control as any,
    name: sourceField,
  }) as string | undefined;

  useEffect(() => {
    if (!isCreate) return;
    const next = sourceValue ? deriveCode(sourceValue) : '';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form.setValue(targetField as any, next as any, { shouldValidate: true });
  }, [isCreate, sourceValue, form, targetField]);
}

export interface MasterCodeFieldProps {
  /**
   * In create mode the field watches `sourceName` (default `name`) and
   * auto-derives `code`. In edit mode the field is left at its loaded value
   * (renames don't change identity).
   */
  isCreate: boolean;
  /** RHF field name for the code. Defaults to `code`. */
  name?: string;
  /** RHF field name to derive from. Defaults to `name`. */
  sourceName?: string;
  label?: string;
  hint?: string;
  required?: boolean;
}

/**
 * The standard "code" input for master forms (ADR-0016 family). The input is
 * always disabled - the value is computed from the `name` field on create
 * (operator types the name, the code follows), and frozen on edit so existing
 * references stay stable. Server enforces uniqueness as before.
 */
export const MasterCodeField = ({
  isCreate,
  name = 'code',
  sourceName = 'name',
  label = 'Code',
  hint = 'Auto-generated from the name (lowercase, underscores). Editable on the server only.',
  required,
}: MasterCodeFieldProps): React.JSX.Element => {
  const { setValue } = useFormContext();
  const sourceValue = useWatch({ name: sourceName }) as string | undefined;

  useEffect(() => {
    if (!isCreate) return;
    setValue(name, sourceValue ? deriveCode(sourceValue) : '', { shouldValidate: true });
  }, [isCreate, sourceValue, name, setValue]);

  return (
    <UzFormField name={name}>
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label={label}
          hint={hint}
          error={error}
          required={required}
          disabled
        />
      )}
    </UzFormField>
  );
};
